import { getJson, sendJson } from '@/lib/api';
import { REPEAT_CLIENTS_PATH } from '@/lib/repeat-clients';

/**
 * Repeat clients email: the rep's own Gmail connection, the compose page's
 * context, and sending. Mirrors the API's `outreach-email` routes.
 */

/** Writing to one client: a full page (Thomas, 2026-10-24). */
export const OUTREACH_EMAIL_ROUTE = `${REPEAT_CLIENTS_PATH}/email/:accountId`;
/** Set when a manager writes to another rep's client. */
export const OWNER_PARAM = 'owner';

export function outreachEmailPath(accountId: string, ownerId?: string): string {
  const path = `${REPEAT_CLIENTS_PATH}/email/${encodeURIComponent(accountId)}`;
  return ownerId ? `${path}?${new URLSearchParams({ [OWNER_PARAM]: ownerId }).toString()}` : path;
}

/** Where Google sends the browser back: `?gmail=connected|error&reason=`. */
export const GMAIL_RESULT_PARAM = 'gmail';
export const GMAIL_RESULT = { connected: 'connected', error: 'error' } as const;

export const GMAIL_STATUS = {
  connected: 'connected',
  needsReconnect: 'needs_reconnect',
  notConnected: 'not_connected',
} as const;
export type GmailStatus = (typeof GMAIL_STATUS)[keyof typeof GMAIL_STATUS];

export interface GmailConnection {
  readonly status: GmailStatus;
  readonly email: string | null;
  readonly connectedAt: string | null;
  /** Whether this server can connect Gmail at all. */
  readonly configured: boolean;
}

/** Why connecting did not go ahead, as the callback reports it. */
const GMAIL_ERROR_MESSAGE: Readonly<Record<string, string>> = {
  denied: 'Gmail was not connected: Google sign-in was canceled.',
  wrong_google_user: 'Gmail was not connected: sign in with your own bestcorporateevents.com address.',
};
export function gmailErrorMessage(reason: string | null): string {
  return (reason && GMAIL_ERROR_MESSAGE[reason]) || 'Gmail was not connected. Try again.';
}

export const GMAIL_QUERY_KEY = ['outreach-email', 'gmail'] as const;

const BASE = '/outreach-email';

export const readGmailConnection = (signal?: AbortSignal): Promise<GmailConnection> =>
  getJson<GmailConnection>(`${BASE}/gmail/connection`, undefined, signal);

/** Starts connecting: sends the browser to Google. */
export async function connectGmail(): Promise<void> {
  const { url } = await sendJson<{ url: string }>('post', `${BASE}/gmail/connect-url`, { returnTo: window.location.origin });
  window.location.assign(url);
}

export const disconnectGmail = (): Promise<void> => sendJson<void>('delete', `${BASE}/gmail/connection`);

export interface ComposeContact {
  readonly id: string;
  readonly name: string;
  readonly firstName: string;
  readonly title: string;
  readonly email: string;
  readonly emailOptOut: boolean;
}

export interface SentEmail {
  readonly id: string;
  readonly sentAt: string;
  readonly from: string;
  readonly to: string;
  readonly subject: string;
  readonly opens: number;
  readonly lastOpenedAt: string | null;
  readonly loggedToSalesforce: boolean;
}

export interface ComposeContext {
  readonly accountId: string;
  readonly accountName: string;
  readonly ownerId: string;
  readonly contacts: readonly ComposeContact[];
  /** Placeholder values for this client, except first_name (per contact). */
  readonly values: Readonly<Record<string, string>>;
  readonly history: readonly SentEmail[];
}

export const composeQueryKey = (accountId: string, ownerId: string | undefined) => ['outreach-email', 'client', accountId, ownerId ?? 'me'] as const;

export const readComposeContext = (accountId: string, ownerId: string | undefined, signal?: AbortSignal): Promise<ComposeContext> =>
  getJson<ComposeContext>(`${BASE}/clients/${encodeURIComponent(accountId)}`, ownerId ? { ownerId } : undefined, signal);

export interface SendEmailInput {
  readonly contactId: string;
  readonly subject: string;
  readonly body: string;
  readonly templateId?: string;
  readonly ownerId?: string;
}

export interface SendResult {
  readonly email: SentEmail;
  /** Why Salesforce did not log it; null when logged. The email still went. */
  readonly salesforceError: string | null;
}

export const sendOutreachEmail = (accountId: string, input: SendEmailInput): Promise<SendResult> =>
  sendJson<SendResult>('post', `${BASE}/clients/${encodeURIComponent(accountId)}/send`, input);

/** The first_name placeholder, filled per contact. */
export const FIRST_NAME_PLACEHOLDER = 'first_name';

const PLACEHOLDER_PATTERN = /\{([a-z][a-z0-9_]*)\}/g;

/**
 * Fills a template for a real client, the way the API's fillPlaceholders
 * does: a placeholder with no value stays as written, so the rep sees the gap.
 *
 * @returns The text and the placeholders still unfilled.
 */
export function fillForClient(template: string, values: Readonly<Record<string, string>>): { readonly text: string; readonly unfilled: readonly string[] } {
  const unfilled = new Set<string>();
  const text = template.replace(PLACEHOLDER_PATTERN, (match, key: string) => {
    const value = values[key]?.trim();
    if (!value) {
      unfilled.add(key);
      return match;
    }
    return value;
  });
  return { text, unfilled: [...unfilled] };
}

/** "Opened 3 times" style summary of an email's opens. */
export function opensLabel(opens: number): string {
  if (opens === 0) return 'Not opened yet';
  return opens === 1 ? 'Opened once' : `Opened ${opens} times`;
}
